import { createSlice } from '@reduxjs/toolkit';

/**
 * Initial state for dashboard management
 */
const initialState = {
  // List of user projects
  projects: [],
  
  // List of scheduled tests
  scheduledTests: [],
  
  // List of pending approval requests
  pendingApprovals: [],
  
  // Loading state for dashboard data fetching
  isLoading: false,
  
  // Initial load state (true after login, false after first data load)
  isInitialLoad: false,
  
  // Error message for dashboard operations
  error: null,
  
  // Timestamp of last successful data fetch
  lastFetched: null,
};

/**
 * Dashboard slice for managing dashboard state
 */
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    /**
     * Set the list of projects
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: array of project objects
     */
    setProjects: (state, action) => {
      state.projects = action.payload;
      state.error = null;
    },

    /**
     * Set the list of scheduled tests
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: array of scheduled test objects
     */
    setScheduledTests: (state, action) => {
      state.scheduledTests = action.payload;
      state.error = null;
    },

    /**
     * Set the list of pending approvals
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: array of pending approval objects
     */
    setPendingApprovals: (state, action) => {
      state.pendingApprovals = action.payload;
      state.error = null;
    },

    /**
     * Set all dashboard data at once
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: { projects, scheduledTests, pendingApprovals }
     */
    setDashboardData: (state, action) => {
      const { projects, scheduledTests, pendingApprovals } = action.payload;
      
      if (projects !== undefined) {
        state.projects = projects;
      }
      if (scheduledTests !== undefined) {
        state.scheduledTests = scheduledTests;
      }
      if (pendingApprovals !== undefined) {
        state.pendingApprovals = pendingApprovals;
      }
      
      state.lastFetched = Date.now();
      state.error = null;
    },

    /**
     * Set the loading state
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: boolean
     */
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    /**
     * Set the initial load state (post-login loading)
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: boolean
     */
    setInitialLoad: (state, action) => {
      state.isInitialLoad = action.payload;
    },

    /**
     * Set an error message
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: error message string or null
     */
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Update a pending approval status
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: { userId, projectId, status }
     */
    updateApprovalStatus: (state, action) => {
      const { userId, projectId, status } = action.payload;
      
      // Find and update the approval in the list
      const approval = state.pendingApprovals.find(
        (a) => a.requested_user_id === userId && a.project_id === projectId
      );
      
      if (approval) {
        approval.status = status;
      }
      
      state.error = null;
    },

    /**
     * Remove an approval from the pending list
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: { userId, projectId }
     */
    removeApproval: (state, action) => {
      const { userId, projectId } = action.payload;
      
      state.pendingApprovals = state.pendingApprovals.filter(
        (a) => !(a.requested_user_id === userId && a.project_id === projectId)
      );
      
      state.error = null;
    },

    /**
     * Reset dashboard state to initial values
     * @param {Object} state - Current state
     */
    resetDashboard: (state) => {
      state.projects = [];
      state.scheduledTests = [];
      state.pendingApprovals = [];
      state.isLoading = false;
      state.isInitialLoad = false;
      state.error = null;
      state.lastFetched = null;
    },
  },
});

// Export actions
export const {
  setProjects,
  setScheduledTests,
  setPendingApprovals,
  setDashboardData,
  setLoading,
  setInitialLoad,
  setError,
  updateApprovalStatus,
  removeApproval,
  resetDashboard,
} = dashboardSlice.actions;

// Selectors
export const selectProjects = (state) => state.dashboard.projects;
export const selectScheduledTests = (state) => state.dashboard.scheduledTests;
export const selectPendingApprovals = (state) => state.dashboard.pendingApprovals;
export const selectIsLoading = (state) => state.dashboard.isLoading;
export const selectIsInitialLoad = (state) => state.dashboard.isInitialLoad;
export const selectError = (state) => state.dashboard.error;
export const selectLastFetched = (state) => state.dashboard.lastFetched;

/**
 * Selector to get dashboard statistics
 */
export const selectDashboardStats = (state) => ({
  totalProjects: state.dashboard.projects.length,
  totalScheduledTests: state.dashboard.scheduledTests.length,
  totalPendingApprovals: state.dashboard.pendingApprovals.length,
});

/**
 * Selector to check if all dashboard data is empty
 */
export const selectIsAllDataEmpty = (state) => {
  return (
    state.dashboard.projects.length === 0 &&
    state.dashboard.scheduledTests.length === 0 &&
    state.dashboard.pendingApprovals.length === 0
  );
};

// Export reducer
export default dashboardSlice.reducer;
